'use strict';

// dom
const {
	h1, a, div, header,
	section, button, span,
	ul, li, br, i, hr, img,
	table, thead, tbody, tr, th, td,
	form, input, label, fieldset, legend,
	select, option, textarea
} = require('iblokz-snabbdom-helpers');

// lib
const dayjs = require('dayjs');
const {obj, str, arr} = require('iblokz-data');
const formUtil = require('../../../util/form');
const eventTime = require('../../../util/event-time');
const {fieldLabel, enumLabel} = require('./label');
const _rl = require('../../../util/resolve-localized');
const {
	LOCALES,
	DEFAULT_LOCALE,
	resolveLocalized,
	writeLocalized
} = _rl.default || _rl;

// fieldTypes
const multiple = require('./multiple');
const file = require('./file');
const wysiwyg = require('./wysiwyg').default ?? require('./wysiwyg');
const items = require('./items');
const urlFile = require('./url-file');

const sub = (o, p, d = false) =>
	o === undefined
		? d
		: (p instanceof Array)
			? o[p[0]] && p.length > 1
				? [sub(o[p[0]], p.slice(1))].map(sO => (sO !== undefined ? sO : d)).pop()
				: o[p[0]]
			: o[p] !== undefined ? o[p] : d;

const schemaInputFieldMap = {
	String: 'text',
	Number: 'number',
	Date: 'datetime-local',
	Boolean: 'checkbox'
};

const keys = o => Object.keys(o);

const RICH_KEYS = ['content', 'article', 'post', 'text', 'about'];

// Do not use obj.switch here: schema fields may have a `default` property, and
// obj.switch treats cases[type].default as the result (e.g. status.default → "draft").
const decorateField = field => {
	if (!field || typeof field !== 'object') return field;
	if ((field.type === 'String' || field.type === 'Mixed')
		&& RICH_KEYS.indexOf(field.name) > -1) {
		return {...field, type: 'RichText'};
	}
	return field;
};

const parseField = (field, name) => (
	(typeof field === 'string')
		? {type: field, name, required: true}
		: (field && typeof field.type === 'string')
			? Object.assign({}, field, {name})
			: field instanceof Array
				? {type: 'Array', name, schema: parseField(field[0])}
				: field instanceof Object
					? {type: 'Object', name, fields: keys(field).map(key => parseField(field[key], [name, key].join('.')))}
					: false
);

const refToCollection = {
	User: 'users',
	Profile: 'profiles',
	Event: 'events',
	Article: 'articles',
	Page: 'pages'
};

const labelKeys = ['fullName', 'email', 'title', 'name', 'path'];

const hashFromRef = (state, ref) => {
	const ns = refToCollection[ref];
	const list = ns && state && state[ns] && state[ns].list;
	if (!list) return {};
	return list.reduce((hash, doc) => {
		const label = labelKeys
			.map(k => {
				const v = doc[k];
				if (v == null) return '';
				if (typeof v === 'string') return v;
				if (typeof v === 'object') return resolveLocalized(v, (state && state.lang) || DEFAULT_LOCALE);
				return String(v);
			})
			.find(Boolean) || String(doc._id);
		return Object.assign(hash, {[doc._id]: label});
	}, {});
};

const getParent = (el, tagName) => ([].concat(tagName).indexOf(el.tagName) === -1)
	? getParent(el.parentNode, tagName) : el;

const inputAddons = (content, field, addons) => addons.length > 0 && field.type === 'String'
	? div('.input-group', [
		content,
		div('.input-group-btn', addons.map(addon =>
			button(`.btn.btn-default[type=button]`, {
				on: {
					click: ev => addon.cb(field, getParent(ev.target, 'DIV').parentNode.querySelector('input').value)
				}
			}, [i(`.fa.${addon.icon}`)])
		))
	])
	: content;

const columnsAndNewItemFromSchema = (schema, i18n) => {
	if (!schema || schema.type !== 'Object' || !schema.fields || !schema.fields.length) return null;
	const columns = schema.fields.map(f => {
		const key = f.name.split('.').pop();
		return {
			key,
			type: f.type,
			label: fieldLabel(i18n, key),
			enum: f.enum instanceof Array ? f.enum : undefined
		};
	});
	const colRank = key => (key === 'type' || key === 'source') ? 0 : (key === 'url' ? 1 : 2);
	columns.sort((a, b) => colRank(a.key) - colRank(b.key));
	const newItem = schema.fields.reduce((o, f) => {
		const key = f.name.split('.').pop();
		let value = '';
		if (f.type === 'Number') value = 0;
		else if (f.enum instanceof Array) value = f.enum[0];
		return {...o, [key]: value};
	}, {});
	return {columns, newItem};
};

const isI18nField = (field, i18nFields) =>
	(i18nFields || []).indexOf(field.name) > -1;

const fieldLangOf = (fieldLangs, fieldName, stateLang) => {
	const fallback = stateLang || DEFAULT_LOCALE;
	const override = fieldLangs && fieldLangs[fieldName];
	if (!override) return fallback;
	// sticky only while global lang matches the base when the override was set
	if (typeof override === 'object' && override.lang) {
		return override.base === fallback ? override.lang : fallback;
	}
	if (typeof override === 'string') return override;
	return fallback;
};

const LOCALE_FLAGS = {
	bg: '/assets/flags/bg.svg',
	en: '/assets/flags/gb.svg'
};

const nextLocale = (active, locales = LOCALES) => {
	const i = locales.indexOf(active);
	return locales[(i + 1) % locales.length] || locales[0];
};

/** Single flag that toggles locale on click (not side-by-side). */
const langSwitcher = ({active, onSelect}) => {
	const loc = active || DEFAULT_LOCALE;
	const next = nextLocale(loc);
	return span('.field-lang-switch', {
		attrs: {
			'data-lang': loc,
			title: `${loc.toUpperCase()} → ${next.toUpperCase()}`,
			role: 'button',
			tabindex: '0',
			'aria-label': `Language ${loc.toUpperCase()}, switch to ${next.toUpperCase()}`
		},
		on: {
			mousedown: ev => ev.preventDefault(),
			dblclick: ev => ev.preventDefault(),
			click: ev => (ev.preventDefault(), ev.stopPropagation(), onSelect(next)),
			keydown: ev => {
				if (ev.key === 'Enter' || ev.key === ' ') {
					ev.preventDefault();
					onSelect(next);
				}
			}
		}
	}, img(`.field-lang-flag[src="${LOCALE_FLAGS[loc]}"][alt="${loc.toUpperCase()}"]`));
};

const formElement = ({
	field, doc, update = () => {}, focused = false, addons = [],
	state, fullActions, i18n = {}, i18nFields = [], fieldLangs = {}, setFieldLang = () => {}
}) => {
	const stateLang = (state && state.lang) || DEFAULT_LOCALE;
	const localized = isI18nField(field, i18nFields);
	const activeLang = fieldLangOf(fieldLangs, field.name, stateLang);

	const collection = state && state.router && state.router.path
		? (state.router.path[0] === 'admin' ? state.router.path[1] : state.router.path[0])
		: null;

	const patchLocalized = text => {
		if (fullActions && collection) {
			fullActions.set([collection, 'doc'], current => {
				const base = current || {};
				return Object.assign({}, base, {
					[field.name]: writeLocalized(base[field.name], activeLang, text)
				});
			});
			return;
		}
		update(field.name.split('.'), writeLocalized(sub(doc, field.name.split('.')), activeLang, text));
	};

	const switcher = localized
		? langSwitcher({
			active: activeLang,
			onSelect: loc => {
				const el = document.activeElement;
				if (el && typeof el.blur === 'function') el.blur();
				setFieldLang(field.name, loc);
			}
		})
		: null;

	return obj.switch(field.type, {
		File: value => file({field, value, update, focused, i18n}),
		RichText: value => {
			const raw = localized ? value : value;
			const content = localized
				? resolveLocalized(raw, activeLang)
				: (value && value.content !== undefined ? value.content : value);
			const richUi = (doc && doc[`_${field.name}Rich`]) || {};
			const richValue = {
				content: content || '',
				pos: richUi.pos || {start: {row: 0, col: 0}, end: {row: 0, col: 1}},
				mode: richUi.mode || 'wysiwyg'
			};
			const richUpdate = (path, next) => {
				if (localized && fullActions && collection) {
					fullActions.set([collection, 'doc'], current => {
						const base = current || {};
						return Object.assign({}, base, {
							[field.name]: writeLocalized(
								base[field.name],
								activeLang,
								next.content
							),
							[`_${field.name}Rich`]: {pos: next.pos, mode: next.mode}
						});
					});
				} else if (localized) {
					update(field.name.split('.'), writeLocalized(
						sub(doc, field.name.split('.')),
						activeLang,
						next.content
					));
				} else {
					update(path, next);
				}
			};
			return wysiwyg({
				field,
				value: richValue,
				update: richUpdate,
				focused,
				addons,
				i18n,
				langSwitcher: switcher,
				contentKey: localized ? activeLang : ''
			});
		},
		Text: value => div('.field', {class: {'field-i18n': localized}}, [
			field.name.split('.').length === 1 ? label([
				fieldLabel(i18n, field.name.split('.').pop()),
				': '
			]) : '',
			div('.field-control', [
				switcher,
				textarea(
					{
						style: {width: '100%', minHeight: '160px', outline: 'none'},
						on: {
							input: ev => localized
								? patchLocalized(ev.target.value)
								: update(field.name.split('.'), ev.target.value)
						},
						attrs: {
							name: localized ? undefined : field.name,
							disabled: field.disabled,
							hidden: field.hidden
						},
						props: {
							value: localized ? resolveLocalized(value, activeLang) : value
						}
					}
				)
			])
		]),
		Mixed: value => localized
			? div('.field.field-i18n', [
				field.name.split('.').length === 1 ? label([
					fieldLabel(i18n, field.name.split('.').pop()),
					': '
				]) : '',
				div('.field-control', [
					switcher,
					input({
						on: {input: ev => patchLocalized(ev.target.value)},
						attrs: {
							type: 'text',
							placeholder: fieldLabel(i18n, field.name.split('.').pop()),
							disabled: field.disabled
						},
						props: {
							value: resolveLocalized(value, activeLang) || ''
						}
					})
				])
			])
			: div('.field', [
				field.name.split('.').length === 1 ? label('.col-sm-2.control-label', [
					fieldLabel(i18n, field.name.split('.').pop()),
					': '
				]) : '',
				textarea(
					{
						on: {input: ev => update(field.name.split('.'), ev.target.value)},
						attrs: {name: field.name, disabled: field.disabled, hidden: field.hidden}
					},
					typeof value === 'string' ? value : JSON.stringify(value)
				)
			]),
		Array: value => field.schema.type === 'String' || field.schema.type === 'ObjectId'
			? multiple({field, value, update, i18n})
			: (state && fullActions && field.schema && field.schema.type === 'Object' && field.schema.fields && field.schema.fields.length
				? (() => {
					const derived = columnsAndNewItemFromSchema(field.schema, i18n);
					const collection = state.router.path[0] === 'admin'
						? state.router.path[1]
						: state.router.path[0];
					return derived ? div('.field', [
						field.name.split('.').length === 1 ? label([fieldLabel(i18n, field.name), ': ']) : '',
						items({
							path: [collection, 'doc', field.name],
							columns: derived.columns,
							newItem: derived.newItem,
							state,
							actions: fullActions,
							i18n
						})
					]) : formElement({
						field: {...field, type: 'Mixed'}, doc, update, focused, addons,
						state, fullActions, i18n, i18nFields, fieldLangs, setFieldLang
					});
				})()
				: formElement({
					field: {...field, type: 'Mixed'}, doc, update, focused, addons,
					state, fullActions, i18n, i18nFields, fieldLangs, setFieldLang
				})),
		default: value => div('.field', {class: {'field-i18n': localized && field.type === 'String'}}, [
			field.name.split('.').length === 1
				? label([
					fieldLabel(i18n, field.name.split('.').pop()),
					': '
				]) : '',
			(field.type === 'String' && field.enum instanceof Array)
				? select({
					on: {change: ev => update(field.name.split('.'), ev.target.value)},
					attrs: {name: field.name, disabled: field.disabled}
				}, [].concat(
						field.enum.map((val, index) => option({
							attrs: {
								value: val,
								selected: typeof value === 'undefined' && index === 0 || val === value
							},
							props: {
								value: val,
								selected: typeof value === 'undefined' && index === 0 || val === value
							}
						}, enumLabel(i18n, field.name.split('.').pop(), val))
					)))
				: (field.type === 'ObjectId')
					? (() => {
						const hash = field.hash instanceof Object
							? field.hash
							: hashFromRef(state, field.ref);
						const selected = value ? String(value) : '';
						const na = (i18n.common && i18n.common.actions && i18n.common.actions.na) || 'n/a';
						return select({
							on: {change: ev => update(field.name.split('.'), ev.target.value)},
							attrs: {name: field.name, disabled: field.disabled}
						}, [].concat(
							option({
								attrs: {value: '', selected: selected === ''},
								props: {value: '', selected: selected === ''}
							}, na),
							Object.keys(hash).map(id =>
								option({
									attrs: {value: id, selected: id === selected},
									props: {value: id, selected: id === selected}
								}, hash[id])
							)
						));
					})()
				: (['avatar', 'poster'].indexOf(field.name) > -1 && state && fullActions)
					? (() => {
						const collection = state.router.path[0] === 'admin'
							? state.router.path[1]
							: state.router.path[0];
						const fileKey = `_${field.name}File`;
						const nameKey = `_${field.name}FileName`;
						const kindKey = `_${field.name}Kind`;
						const kind = doc[kindKey] || (doc[fileKey] ? 'file' : 'url');
						const patchDoc = patch => fullActions.set(
							[collection, 'doc'],
							Object.assign({}, doc, patch)
						);
						return urlFile({
							kind,
							url: value || '',
							fileName: doc[nameKey] || '',
							accept: 'image/*',
							onKind: next => patchDoc({
								[kindKey]: next,
								[fileKey]: next === 'file' ? doc[fileKey] : undefined,
								[nameKey]: next === 'file' ? doc[nameKey] : undefined
							}),
							onUrl: v => patchDoc({[field.name]: v, [kindKey]: 'url'}),
							onFile: files => {
								const f = files[0];
								if (!f) return;
								patchDoc({
									[kindKey]: 'file',
									[fileKey]: f,
									[nameKey]: f.name
								});
							},
							onClear: () => patchDoc({
								[field.name]: '',
								[kindKey]: kind,
								[fileKey]: undefined,
								[nameKey]: undefined
							})
						});
					})()
				: (localized
					? div('.field-control', [
						switcher,
						input({
							on: {input: ev => patchLocalized(ev.target.value)},
							attrs: {
								type: 'text',
								placeholder: fieldLabel(i18n, field.name.split('.').pop()),
								disabled: field.disabled
							},
							props: {
								value: resolveLocalized(value, activeLang) || ''
							}
						})
					])
					: input({
						on: {input: ev => update(field.name.split('.'), ev.target.value)},
						attrs: {
							name: field.name,
							type: field.name.match('password')
								? 'password'
								: field.name.match(/(^|\.)email$/)
									? 'email'
									: schemaInputFieldMap[field.type],
							step: 0.01,
							placeholder: fieldLabel(i18n, field.name.split('.').pop()),
							checked: field.type === 'Boolean' && value === true,
							disabled: field.disabled
						},
						props: {
							value: field.type === 'Date'
								? eventTime.toInputValue(value, doc && doc.timezone)
								: value || ''
						}
					}))
		]),
		Object: () => div('.field', [].concat(
			label('', [
				fieldLabel(i18n, field.name.split('.').pop()),
				': '
			]),
			field.fields.map(f => formElement({
				field: f, doc, update, focused, addons, state, fullActions, i18n,
				i18nFields, fieldLangs, setFieldLang
			}))
		))
	})(sub(doc, field.name.split('.'), field.type === 'Array' ? [] : ''));
};

module.exports = ({schema, doc = {}, sel = '',
	noactions = false, actions: {save, cancel, update = () => {}},
	focused = false,
	addons = [],
	state,
	fullActions,
	i18n = {},
	i18nFields = [],
	fieldLangs = {},
	setFieldLang = () => {}
}) => form(sel, {
	on: {
		submit: ev => (
				ev.preventDefault(),
				save({data: formUtil.toData(ev.target), ev})
			)
	}
}, [].concat(
	keys(schema)
		.map(key => parseField(schema[key], key))
			.map(decorateField)
      .reduce((els, field) => [].concat(
				els,
				formElement({
					field, doc, update, focused: focused === field.name, addons,
					state, fullActions, i18n, i18nFields, fieldLangs, setFieldLang
				})
			), []),
	!noactions ? div(`.buttons`, [
		button(`#edit-cancel.btn.btn-white[type="button"]`, {on: {click: ev => cancel({ev})}},
			(i18n.common && i18n.common.actions && i18n.common.actions.cancel) || 'Cancel'),
		' ',
		button(`#edit-save.btn.btn-primary[type="sumbit"]`,
			(i18n.common && i18n.common.actions && i18n.common.actions.save) || 'Save')
	]) : []
));
